// ============================================
// MQTT 数据源适配器
// 轻量级 MQTT-over-WebSocket 客户端
// 支持 CONNECT/SUBSCRIBE/PUBLISH/PING
// ============================================

import type { DatasourceAdapter } from '../../types/databinding';
import type { DatasourceConfig, MqttConfig } from '../../types/designer';
import { EventEmitter } from '../EventEmitter';

// MQTT 控制包类型
const MQTT_CONNECT = 1;
const MQTT_CONNACK = 2;
const MQTT_PUBLISH = 3;
const MQTT_SUBSCRIBE = 8;
const MQTT_SUBACK = 9;
const MQTT_PINGREQ = 12;
const MQTT_PINGRESP = 13;
const MQTT_DISCONNECT = 14;

export class MqttAdapter extends EventEmitter implements DatasourceAdapter {
  readonly type = 'mqtt' as const;
  private status: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private packetId = 0;
  private config: DatasourceConfig | null = null;
  private subscriptions: Map<string, Set<(data: any) => void>> = new Map();
  private pendingSubacks: Map<number, { topic: string; qos: number }> = new Map();
  /** 2026-09-14 修复（FE-56）：用户主动断开标记（区别于连接失败/断线） */
  private manualClose = false;

  async connect(config: DatasourceConfig): Promise<void> {
    if (config.type !== 'mqtt') throw new Error('Invalid config type');
    this.config = config;
    this.manualClose = false;
    this.status = 'connecting';
    this.emit('status:change', this.status);

    const mqttConfig = config.config as MqttConfig;
    const wsUrl = mqttConfig.broker.replace(/^mqtt:\/\//, 'ws://').replace(/^mqtts:\/\//, 'wss://');
    const port = mqttConfig.port || 8083;
    // 2026-09-14 修复（FE-29）：broker 已带端口（如 mqtt://127.0.0.1:1883）时不再追加端口
    // （原实现无条件 `${wsUrl}:${port}/mqtt` → 拼成 ws://127.0.0.1:1883:8083/mqtt 无效地址）
    const bareHost = wsUrl.replace(/^wss?:\/\//, '').split('/')[0];
    const hasPort = /:\d+$/.test(bareHost);
    const url = hasPort ? `${wsUrl}/mqtt` : `${wsUrl}:${port}/mqtt`;

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          // 发送 CONNECT 包
          this.sendConnect(mqttConfig);
        };

        this.ws.onmessage = (event) => {
          if (event.data instanceof ArrayBuffer) {
            this.handlePacket(new Uint8Array(event.data));
          } else if (typeof event.data === 'string') {
            // MQTT v5 某些 broker 可能发送文本消息
            try {
              const data = JSON.parse(event.data);
              this.handleMessage(data);
            } catch {}
          }
        };

        this.ws.onerror = () => {
          this.status = 'error';
          this.emit('status:change', this.status);
          reject(new Error('MQTT WebSocket connection failed'));
        };

        this.ws.onclose = () => {
          // 2026-09-14 修复（FE-56）：同 WebSocketAdapter——失败后保持 error（不被 disconnected 覆盖），手动断开不重连
          if (!this.manualClose && this.status === 'error') {
            this.emit('status:change', this.status);
            if (this.pingTimer) clearInterval(this.pingTimer);
            return;
          }
          this.status = 'disconnected';
          this.emit('status:change', this.status);
          if (this.pingTimer) clearInterval(this.pingTimer);
          if (!this.manualClose) this.attemptReconnect();
        };

        // CONNECT 成功后的处理
        // 2026-09-14 修复（FE-56）：CONNACK 超时（socket 连上但 broker 不回包）——10s 收敛为 error，不卡 connecting
        const connackTimeout = setTimeout(() => {
          if (this.status === 'connecting') {
            this.status = 'error';
            this.emit('status:change', this.status);
            reject(new Error('MQTT CONNACK 超时（broker 无响应）'));
          }
        }, 10000);
        this.once('connack', () => {
          clearTimeout(connackTimeout);
          this.status = 'connected';
          this.reconnectAttempts = 0;
          this.emit('status:change', this.status);

          // 启动心跳
          this.pingTimer = setInterval(() => {
            this.sendPing();
          }, 30000);

          resolve();
        });
      } catch (err: any) {
        this.status = 'error';
        this.emit('status:change', this.status);
        reject(err);
      }
    });
  }

  disconnect(): void {
    // 2026-09-14 修复（FE-56）：标记主动断开——避免 onclose 触发自动重连
    this.manualClose = true;
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.sendDisconnect();
      this.ws.close();
      this.ws = null;
    }
    this.subscriptions.clear();
    this.pendingSubacks.clear();
    this.status = 'disconnected';
    this.config = null;
    this.emit('status:change', this.status);
  }

  subscribe(topic: string, callback: (data: any) => void, qos: 0 | 1 | 2 = 0): void {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic)!.add(callback);

    // 发送 SUBSCRIBE 包
    const packetId = ++this.packetId;
    this.pendingSubacks.set(packetId, { topic, qos });
    this.sendSubscribe(topic, qos, packetId);
    this.emit('subscribe', { topic });
  }

  unsubscribe(topic: string): void {
    this.subscriptions.delete(topic);
    this.emit('unsubscribe', { topic });
  }

  publish(topic: string, data: any, qos: 0 | 1 | 2 = 0): void {
    const payload = typeof data === 'string' ? data : JSON.stringify(data);
    this.sendPublish(topic, payload, qos);
  }

  getStatus() {
    return this.status;
  }

  // ============================================
  // MQTT 包构造
  // ============================================

  private sendConnect(config: MqttConfig): void {
    const clientId = `dtwin_${Math.random().toString(36).slice(2, 10)}`;
    const username = config.username;
    const password = config.password;

    // 计算剩余长度
    let connectFlags = 0;
    if (username) connectFlags |= 0x80;
    if (password) connectFlags |= 0x40;
    connectFlags |= 0x02; // Clean Session

    // 可变头 + 负载
    const variableHeader = new Uint8Array([
      0x00, 0x04, // 协议名长度
      0x4D, 0x51, 0x54, 0x54, // "MQTT"
      0x04, // 协议级别 (MQTT 3.1.1)
      connectFlags,
      0x00, 0x3C, // Keep Alive = 60s
    ]);

    // 构造 payload
    const encoder = new TextEncoder();
    const clientBytes = encoder.encode(clientId);
    let payloadLength = 2 + clientBytes.length;
    let usernameBytes: Uint8Array | null = null;
    let passwordBytes: Uint8Array | null = null;

    if (username) {
      usernameBytes = encoder.encode(username);
      payloadLength += 2 + usernameBytes.length;
    }
    if (password) {
      passwordBytes = encoder.encode(password);
      payloadLength += 2 + passwordBytes.length;
    }

    const packet = new Uint8Array(1 + variableHeader.length + payloadLength);
    packet[0] = MQTT_CONNECT << 4;
    let offset = this.encodeRemainingLength(packet, 1, variableHeader.length + payloadLength);
    packet.set(variableHeader, offset);
    offset += variableHeader.length;

    // Client ID
    packet[offset++] = clientBytes.length >> 8;
    packet[offset++] = clientBytes.length & 0xff;
    packet.set(clientBytes, offset);
    offset += clientBytes.length;

    // Username
    if (usernameBytes) {
      packet[offset++] = usernameBytes.length >> 8;
      packet[offset++] = usernameBytes.length & 0xff;
      packet.set(usernameBytes, offset);
      offset += usernameBytes.length;
    }

    // Password
    if (passwordBytes) {
      packet[offset++] = passwordBytes.length >> 8;
      packet[offset++] = passwordBytes.length & 0xff;
      packet.set(passwordBytes, offset);
    }

    this.send(packet);
  }

  private sendSubscribe(topic: string, qos: number, packetId: number): void {
    const encoder = new TextEncoder();
    const topicBytes = encoder.encode(topic);
    const remainingLength = 2 + 2 + topicBytes.length + 1; // packetId + topicLen + topic + qos

    const packet = new Uint8Array(1 + 4 + remainingLength);
    packet[0] = MQTT_SUBSCRIBE << 4 | 0x02;
    let offset = this.encodeRemainingLength(packet, 1, remainingLength);

    // Packet ID
    packet[offset++] = packetId >> 8;
    packet[offset++] = packetId & 0xff;

    // Topic
    packet[offset++] = topicBytes.length >> 8;
    packet[offset++] = topicBytes.length & 0xff;
    packet.set(topicBytes, offset);
    offset += topicBytes.length;

    // QoS
    packet[offset] = qos;

    this.send(packet);
  }

  private sendPublish(topic: string, payload: string, qos: number): void {
    const encoder = new TextEncoder();
    const topicBytes = encoder.encode(topic);
    const payloadBytes = encoder.encode(payload);
    const remainingLength = 2 + topicBytes.length + (qos > 0 ? 2 : 0) + payloadBytes.length;

    const packet = new Uint8Array(1 + 4 + remainingLength);
    packet[0] = MQTT_PUBLISH << 4 | (qos << 1);
    let offset = this.encodeRemainingLength(packet, 1, remainingLength);

    // Topic
    packet[offset++] = topicBytes.length >> 8;
    packet[offset++] = topicBytes.length & 0xff;
    packet.set(topicBytes, offset);
    offset += topicBytes.length;

    // Packet ID (only for QoS 1 or 2)
    if (qos > 0) {
      const pid = ++this.packetId;
      packet[offset++] = pid >> 8;
      packet[offset++] = pid & 0xff;
    }

    // Payload
    packet.set(payloadBytes, offset);

    this.send(packet);
  }

  private sendPing(): void {
    this.send(new Uint8Array([MQTT_PINGREQ << 4, 0]));
  }

  private sendDisconnect(): void {
    this.send(new Uint8Array([MQTT_DISCONNECT << 4, 0]));
  }

  // ============================================
  // MQTT 包解析
  // ============================================

  private handlePacket(data: Uint8Array): void {
    if (data.length < 2) return;
    const type = (data[0] >> 4) & 0x0f;

    switch (type) {
      case MQTT_CONNACK:
        this.handleConnack(data);
        break;
      case MQTT_SUBACK:
        this.handleSuback(data);
        break;
      case MQTT_PUBLISH:
        this.handlePublish(data);
        break;
      case MQTT_PINGRESP:
        // 心跳响应
        break;
    }
  }

  private handleConnack(data: Uint8Array): void {
    if (data.length >= 4) {
      const returnCode = data[3];
      if (returnCode === 0) {
        this.emit('connack');
      } else {
        this.emit('error', `CONNACK return code: ${returnCode}`);
      }
    }
  }

  private handleSuback(data: Uint8Array): void {
    if (data.length >= 4) {
      const packetId = (data[2] << 8) | data[3];
      this.pendingSubacks.delete(packetId);
      this.emit('suback', { packetId });
    }
  }

  private handlePublish(data: Uint8Array): void {
    const flags = data[0] & 0x0f;
    const qos = (flags >> 1) & 0x03;

    let offset = 1;
    // 解码剩余长度
    let multiplier = 1;
    let remainingLength = 0;
    while (offset < data.length) {
      const byte = data[offset++];
      remainingLength += (byte & 0x7f) * multiplier;
      if ((byte & 0x80) === 0) break;
      multiplier *= 128;
    }

    // Topic
    const topicLen = (data[offset] << 8) | data[offset + 1];
    offset += 2;
    const topicBytes = data.slice(offset, offset + topicLen);
    const topic = new TextDecoder().decode(topicBytes);
    offset += topicLen;

    // Packet ID (QoS > 0)
    if (qos > 0) {
      offset += 2;
    }

    // Payload
    const payloadBytes = data.slice(offset);
    const payload = new TextDecoder().decode(payloadBytes);

    let parsed: any;
    try {
      parsed = JSON.parse(payload);
    } catch {
      parsed = payload;
    }

    this.handleMessage({ topic, ...parsed });
  }

  private handleMessage(data: any): void {
    const topic = data.topic || 'default';
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      callbacks.forEach((cb) => cb(data));
    }

    // 通配订阅
    const wildcardCallbacks = this.subscriptions.get('*');
    if (wildcardCallbacks) {
      wildcardCallbacks.forEach((cb) => cb(data));
    }

    this.emit('message', data);
  }

  // ============================================
  // 工具方法
  // ============================================

  private send(data: Uint8Array): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data.slice().buffer);
    }
  }

  private encodeRemainingLength(packet: Uint8Array, offset: number, length: number): number {
    let pos = offset;
    do {
      let byte = length % 128;
      length = Math.floor(length / 128);
      if (length > 0) byte |= 0x80;
      packet[pos++] = byte;
    } while (length > 0);
    return pos;
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('error', 'Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = 3000 * Math.min(this.reconnectAttempts, 5);

    this.reconnectTimer = setTimeout(() => {
      if (this.config) {
        this.connect(this.config).catch(() => {});
      }
    }, delay);
  }
}
